import { NEllipsis, NPopover } from 'naive-ui';
import _ from 'lodash';
import { findPage as findAccount } from '@/service/api/sys/account';
import { findPage as findStCategory } from '@/service/api/st/st-category';
import { findPage as findStSurveyQuestion } from '@/service/api/st/st-survey-question';
import { findPage as findStSurveyOption } from '@/service/api/st/st-survey-option';
import { findPage as findStSurvey } from '@/service/api/st/st-survey';
import { findAll as findStCategorySection } from '@/service/api/st/st-category-section';
import { findPage as findFittingsItem } from '@/service/api/st/st-solution-fittings-item';
import { findPage as findSolutionService } from '@/service/api/st/st-solution-service';
import { findAll as findRole } from '@/service/api/sys/role';
import { findAll as findDepartment } from '@/service/api/sys/department';
import { findAll as findMenu } from '@/service/api/sys/menu';
import { findAll as findGenEntity } from '@/service/api/gen/gen-entity';
import { findAll as findGenEntityField } from '@/service/api/gen/gen-entity-field';
import { findAll as findAttachmentType } from '@/service/api/base/biz-attachment-type';
import { findAll as findDataFilterGroup } from '@/service/api/sys/data-filter-group';
import { findAll as findCustomerInfo } from '@/service/api/base/customer-info';
import { findAll as findFinanceWallet } from '@/service/api/finance/finance-wallet';
import { findAll as findProductInfo } from '@/service/api/base/product-info';
import { findAll as findInventoryInfo } from '@/service/api/inventory/inventory-info';
import { findAll as findLectureCategory } from '@/service/api/school/lecture-category';
import { findAll as findLectureCourse } from '@/service/api/school/lecture-course';
import { findAll as findVehicleBrand } from '@/service/api/base/vehicle-brand';
import { findAll as findVehicleInfo } from '@/service/api/base/vehicle-info';
import { findAll as findVehicleOem } from '@/service/api/base/vehicle-oem';
import { findAll as findConsumer } from '@/service/api/store/member-info';
import { findAll as findDistrictData } from '@/service/api/common/district-data';
import { findAll as findStoreInfo } from '@/service/api/store/store-info';
import { findAll as findProductSpu } from '@/service/api/base/product-spu';
import { findAll as findProductSku } from '@/service/api/base/product-sku';
import { findAll as findStoreSupplierInfo } from '@/service/api/store/store-supplier-info';
import { findAll as findMaterialType } from '@/service/api/store/material-type';
import { findAll as findMaterialItem } from '@/service/api/store/material-item';
import { findAll as findVehicleSeries } from '@/service/api/base/vehicle-series';
import { findAll as findServiceType } from '@/service/api/store/service-type';
import { findAll as findServiceItem } from '@/service/api/store/service-item';
import { findAll as findServicePackage } from '@/service/api/store/service-package';
import { findAll as findStoreInventory } from '@/service/api/inventory/store-inventory-info';
import { findAll as findDictData } from '@/service/api/common/dict-data';
import { findAll as findVehicles } from '@/service/api/store/member-vehicle-info';
import { findAll as findStoreSaleOrder } from '@/service/api/order/store-sale-order';
import { findAll as findMiniAppTemplate } from '@/service/api/sys/mini-app-template';
import { findAll as findMiniAppConfig } from '@/service/api/sys/mini-app-config';
import { findAll as findWarehouse } from '@/service/api/base/warehouse';
import type { RemoteOptionsConfig } from '@/hooks/common/remote-select';
import { buildTreeData } from '@/utils/common';
import type { Api } from '@/typings/api';

export type OptionKeys =
  | 'Account'
  | 'Role'
  | 'Department'
  | 'Menu'
  | 'DictData'
  | 'GenEntityMain'
  | 'GenEntityField'
  | 'AttachmentType'
  | 'DataFilterGroup'
  | 'CustomerInfo'
  | 'FinanceWallet'
  | 'ProductInfo'
  | 'ProductInfoCascader'
  | 'InventoryInfo'
  | 'VehicleOem'
  | 'VehicleBrand'
  | 'VehicleInfo'
  | 'VehicleSeries'
  | 'LectureCategory'
  | 'LectureCourse'
  | 'StoreInfo'
  | 'StCategory'
  | 'StCategorySection'
  | 'StSurvey'
  | 'StSurveyQuestion'
  | 'StSurveyOption'
  | 'StSolutionFittingsItem'
  | 'StSolutionService'
  | 'ProductSpu'
  | 'ProductSku'
  | 'ProductSkuCode'
  | 'MemberInfo'
  | 'MemberVehicleInfo'
  | 'StoreSupplierInfo'
  | 'MaterialType'
  | 'MaterialItem'
  | 'ServiceType'
  | 'ServiceItem'
  | 'ServicePackage'
  | 'StoreInventoryInfo'
  | 'StoreSaleOrder'
  | 'DistrictData'
  | 'FilmPurchaseOrder'
  | 'FilmPurchaseChildOrder'
  | 'FilmPurchaseGoodsItem'
  | 'MiniAppConfig'
  | 'MiniAppTemplate'
  | 'Warehouse';

/** 供下拉或多选等组件使用的选项 */
const ApiSelectConfig: Record<string, RemoteOptionsConfig> = {
  /** 选择销售项目模版 */
  StCategorySection: {
    apiFn: findStCategorySection,
    labelField: 'title',
    props: {
      valueField: 'id'
    },
    apiParams: {
      column: [
        'category.name',
        'customer.customerName',
        'title',
        'showTitle',
        'orderIndex',
        'width',
        'height',
        'enable',
        'needLogin',
        'contentType',
        'url',
        'content',
        'createTime',
        'createUser.userName',
        'updateTime',
        'updateUser.userName',
        'id'
      ],
      filter: [],
      order: [
        {
          column: 'orderIndex',
          sort: true
        }
      ]
    }
  },
  /** 选择销售项目类别 */
  StCategory: {
    apiFn: findStCategory,
    labelField: 'name',
    props: {
      valueField: 'id'
    },
    apiParams: {
      size: 50,
      column: [
        'customer.customerName',
        'name',
        'parentServiceType.serviceTypeName',
        'bannerTitle',
        'showBannerTitle',
        'orderIndex',
        'enable',
        'needLogin',
        'createTime',
        'createUser.userName',
        'updateTime',
        'updateUser.userName',
        'id'
      ],
      filter: []
      // order: [
      //   {
      //     column: '',
      //     sort: true
      //   }
      // ]
    }
  },
  /** 选择问卷 */
  StSurvey: {
    apiFn: findStSurvey,
    labelField: 'title',
    props: {
      valueField: 'id'
    },
    apiParams: {
      size: 50,
      column: [
        'customer.customerName',
        'category.name',
        'subCategory.name',
        'enable',
        'title',
        'version',
        'createTime',
        'createUser.userName',
        'updateTime',
        'updateUser.userName',
        'id'
      ],
      filter: []
      // order: [
      //   {
      //     column: 'userName',
      //     sort: true
      //   }
      // ]
    }
  },
  /** 选择问题 */
  StSurveyQuestion: {
    apiFn: findStSurveyQuestion,
    labelField: 'question',
    props: {
      valueField: 'id'
    },
    apiParams: {
      size: 50,
      column: [
        'survey.title',
        'enable',
        'type',
        'question',
        'orderIndex',
        'required',
        'createTime',
        'createUser.userName',
        'updateTime',
        'updateUser.userName',
        'id'
      ],
      filter: []
      // order: [
      //   {
      //     column: 'userName',
      //     sort: true
      //   }
      // ]
    }
  },
  /** 选择题目选项 */
  StSurveyOption: {
    apiFn: findStSurveyOption,
    labelField: 'name',
    props: {
      valueField: 'id'
    },
    apiParams: {
      size: 50,
      column: [
        'customer.customerName',
        'name',
        'createTime',
        'createUser.userName',
        'updateTime',
        'updateUser.userName',
        'id'
      ],
      filter: []
      // order: [
      //   {
      //     column: 'userName',
      //     sort: true
      //   }
      // ]
    }
  },
  /** 选择其他配件 */
  StSolutionFittingsItem: {
    apiFn: findFittingsItem,
    labelField: 'fittingsItemList.productSku.productName',
    props: {
      valueField: 'id'
    },
    apiParams: {
      size: 50,
      column: [
        'fittingsItemList.productSku.productName',
        'createTime',
        'createUser.userName',
        'updateTime',
        'updateUser.userName',
        'id'
      ],
      filter: []
      // order: [
      //   {
      //     column: 'userName',
      //     sort: true
      //   }
      // ]
    }
  },
  /** 选择服务项目 */
  StSolutionService: {
    apiFn: findSolutionService,
    labelField: 'servicePackageList.serviceType.serviceTypeName',
    props: {
      valueField: 'id'
    },
    apiParams: {
      size: 50,
      column: [
        'servicePackageList.serviceType.serviceTypeName',
        'createTime',
        'createUser.userName',
        'updateTime',
        'updateUser.userName',
        'id'
      ],
      filter: []
      // order: [
      //   {
      //     column: 'userName',
      //     sort: true
      //   }
      // ]
    }
  },
  /** 选择用户 */
  Account: {
    apiFn: findAccount,
    labelField: 'userName',
    props: {
      valueField: 'id'
    },
    apiParams: {
      size: 50,
      column: ['id', 'userName', 'rolesName', 'departmentList.id', 'departmentList.departmentName'],
      filter: [],
      order: [
        {
          column: 'userName',
          sort: true
        }
      ]
    }
  },
  /** 选择角色 */
  Role: {
    apiFn: findRole,
    labelField: 'roleName',
    props: {
      valueField: 'id'
    },
    apiParams: {
      column: ['id', 'roleName', 'roleCode', 'roleType'],
      filter: []
    }
  },
  /** 选择部门 */
  Department: {
    apiFn: findDepartment,
    labelField: 'departmentName',
    props: {
      valueField: 'id',
      keyField: 'id'
    },
    apiParams: {
      column: ['id', 'departmentName', 'departmentCode', 'departmentType', 'parentDepartment.id'],
      filter: [],
      order: [
        {
          column: 'orderIndex',
          sort: true
        }
      ]
    },
    transformer: (data: any[]) => {
      return buildTreeData(data, {
        parentKey: 'parentDepartment.id',
        childrenKey: 'children',
        keyField: 'id'
      });
    }
  },
  /** 选择菜单 */
  Menu: {
    apiFn: findMenu,
    labelField: 'menuName',
    parentField: 'parentMenu.id',
    props: {
      valueField: 'id',
      keyField: 'id'
    },
    apiParams: {
      column: ['id', 'menuName', 'parentMenu.id', 'menuType', 'menuTableGroupList.id', 'menuTableGroupList.groupName'],
      filter: [],
      order: [
        {
          column: 'orderIndex',
          sort: true
        }
      ]
    },
    transformer: (data: any[]) => {
      return buildTreeData(data, {
        parentKey: 'parentMenu.id',
        childrenKey: 'children',
        keyField: 'id'
      });
    }
  },
  /** 选择实体类 */
  GenEntityMain: {
    apiFn: findGenEntity,
    labelField: 'entityName',
    props: {
      valueField: 'id',
      clearable: true,
      filterable: true,
      remote: true
    },
    apiParams: {
      size: 50,
      column: ['id', 'entityName', 'entityDesc'],
      filter: []
    }
  },
  /** 选择实体字段 */
  GenEntityField: {
    apiFn: findGenEntityField,
    labelField: 'fieldName',
    props: {
      valueField: 'id',
      clearable: true,
      filterable: true,
      remote: true
    },
    apiParams: {
      size: 50,
      column: ['id', 'fieldName', 'fieldDesc', 'fieldType', 'joinEntity.entityName'],
      filter: []
    }
  },
  /** 选择客户 */
  CustomerInfo: {
    apiFn: findCustomerInfo,
    labelField: 'customerName',
    props: {
      valueField: 'id',
      clearable: true,
      filterable: true,
      remote: true
    },
    apiParams: {
      size: 1000,
      column: ['id', 'customerName', 'address', 'department.id', 'isStore', 'city.id', 'city.name', 'address'],
      filter: []
    }
  },
  /** 选择附件类型 */
  AttachmentType: {
    apiFn: findAttachmentType,
    labelField: 'typeName',
    props: {
      valueField: 'entityName',
      renderLabel: (row: any) => {
        return row.entityDesc;
      }
    },
    apiParams: {
      size: 50,
      column: ['id', 'genEntityMain.entityDesc', 'genEntityMain.entityName'],
      filter: [
        {
          column: 'typeCode',
          type: 'andNeq',
          // 值
          value: 'cost_measure_amount'
        }
      ],
      order: [
        {
          column: 'genEntityMain.entityName',
          sort: true
        }
      ]
    },
    transformer: (data: Api.Base.BizAttachmentType[]) => {
      const newData: any[] = [];
      data.forEach(item => {
        if (newData.length === 0) {
          newData.push({ entityDesc: item.genEntityMain.entityDesc, entityName: item.genEntityMain.entityName });
        } else {
          const isFind = newData.find(_item => {
            return _item.entityDesc === item.genEntityMain.entityDesc;
          });
          if (!isFind) {
            newData.push({ entityDesc: item.genEntityMain.entityDesc, entityName: item.genEntityMain.entityName });
          }
        }
      });
      return newData;
    }
  },
  /** 数据规则组 */
  DataFilterGroup: {
    apiFn: findDataFilterGroup,
    labelField: 'name',
    props: {
      valueField: 'id',
      clearable: true,
      remote: true
    },
    apiParams: {
      size: 500,
      column: ['id', 'name'],
      filter: [
        {
          column: 'enable',
          type: 'andEq',
          value: true
        }
      ],
      order: [
        {
          column: 'genEntityMain.entityName',
          sort: true
        }
      ]
    }
  },
  /** 数据字典 */
  DictData: {
    apiFn: findDictData,
    labelField: 'label',
    props: {
      valueField: 'id',
      keyField: 'id',
      childrenField: 'childDictDataList'
    },
    apiParams: {
      column: ['id', 'label', 'orderIndex', 'parentDictData.id'],
      filter: [],
      order: [
        {
          column: 'orderIndex',
          sort: true
        }
      ]
    },
    transformer: (data: any[]) => {
      return buildTreeData(data, {
        parentKey: 'parentDictData.id',
        childrenKey: 'childDictDataList',
        keyField: 'id',
        clearEmptyChildren: true
      });
    }
  },
  /** 选择钱包账户 */
  FinanceWallet: {
    apiFn: findFinanceWallet,
    labelField: 'userName',
    props: {
      valueField: 'id'
    },
    apiParams: {
      size: 50,
      column: ['id', 'account.userName'],
      filter: [],
      order: [
        {
          column: 'account.userName',
          sort: true
        }
      ]
    }
  },
  /** 选择产品信息 */
  ProductInfo: {
    apiFn: findProductInfo,
    labelField: 'productName.label',
    props: {
      valueField: 'id',
      renderLabel: (option: any) => {
        return (
          // <NEllipsis>
          //   {[option.productName?.label, option.productVersion?.label, option.productSpec?.label]
          //     .filter(Boolean)
          //     .join('/')}
          // </NEllipsis>
          <NPopover
            trigger="hover"
            v-slots={{
              trigger: () =>
                [option.productName?.label, option.productVersion?.label, option.productSpec?.label]
                  .filter(Boolean)
                  .join(' / ')
            }}
          >
            <span>
              {[option.productName?.label, option.productVersion?.label, option.productSpec?.label]
                .filter(Boolean)
                .join(' / ')}
            </span>
          </NPopover>
        );
      }
    },
    apiParams: {
      size: 50,
      column: ['id', 'articleNumber', 'productName.label', 'productVersion.label', 'productSpec.label'],
      filter: [],
      order: [
        {
          column: 'productName',
          sort: true
        }
      ]
    },
    handleSearchFilter: searchText => {
      return [
        {
          column: null,
          type: 'andOr',
          value: [
            {
              column: 'productName.label',
              type: 'andLike',
              value: `%${searchText}%`
            },
            {
              column: 'productVersion.label',
              type: 'andLike',
              value: `%${searchText}%`
            },
            {
              column: 'productSpec.label',
              type: 'andLike',
              value: `%${searchText}%`
            },
            {
              column: 'articleNumber',
              type: 'andLike',
              value: `%${searchText}%`
            }
          ]
        }
      ];
    }
  },
  /** 选择库存信息 */
  InventoryInfo: {
    apiFn: findInventoryInfo,
    labelField: 'reelNumber',
    props: {
      valueField: 'id'
    },
    apiParams: {
      size: 50,
      column: ['id', 'reelNumber'],
      filter: [],
      order: [
        {
          column: 'reelNumber',
          sort: true
        }
      ]
    }
  },
  /** 选择课程分类 */
  LectureCategory: {
    apiFn: findLectureCategory,
    labelField: 'categoryName',
    props: {
      valueField: 'id'
    },
    apiParams: {
      size: 50,
      column: ['id', 'categoryName'],
      filter: [],
      order: [
        {
          column: 'categoryName',
          sort: true
        }
      ]
    }
  },
  /** 选择课程 */
  LectureCourse: {
    apiFn: findLectureCourse,
    labelField: 'courseName',
    props: {
      valueField: 'id'
    },
    apiParams: {
      size: 50,
      column: ['id', 'courseName'],
      filter: [],
      order: [
        {
          column: 'courseName',
          sort: true
        }
      ]
    }
  },

  /** 选择车型品牌 */
  VehicleBrand: {
    apiFn: findVehicleBrand,
    labelField: 'brandName',
    props: {
      valueField: 'id',
      clearable: true,
      filterable: true
    },
    apiParams: {
      column: ['id', 'brandName'],
      filter: [],
      order: [
        {
          column: 'orderIndex',
          sort: true
        }
      ]
    }
  },
  /** 选择车辆型号 */
  VehicleInfo: {
    apiFn: findVehicleInfo,
    labelField: 'vehicleModel',
    props: {
      valueField: 'id',
      clearable: true,
      filterable: true
    },
    apiParams: {
      column: ['id', 'vehicleModel', 'year'],
      filter: [],
      order: [
        {
          column: 'orderIndex',
          sort: true
        }
      ]
    }
  },
  /** 选择车型厂商 */
  VehicleOem: {
    apiFn: findVehicleOem,
    labelField: 'oemName',
    props: {
      valueField: 'id',
      clearable: true,
      filterable: true
    },
    apiParams: {
      column: ['id', 'oemName'],
      filter: [],
      order: [
        {
          column: 'oemName',
          sort: true
        }
      ]
    }
  },
  /** 选择车辆车系 */
  VehicleSeries: {
    apiFn: findVehicleSeries,
    labelField: 'seriesName',
    props: {
      valueField: 'id',
      clearable: true,
      filterable: true
    },
    apiParams: {
      column: ['id', 'seriesName'],
      filter: [],
      order: [
        {
          column: 'orderIndex',
          sort: true
        }
      ]
    }
  },
  /** 选择门店 */
  StoreInfo: {
    apiFn: findStoreInfo,
    labelField: 'storeName',
    props: {
      valueField: 'id'
    },
    apiParams: {
      size: 50,
      column: ['id', 'storeName'],
      filter: [],
      order: [
        {
          column: 'storeName',
          sort: true
        }
      ]
    }
  },
  /** 选择门店供应商 */
  StoreSupplierInfo: {
    apiFn: findStoreSupplierInfo,
    labelField: 'supplierName',
    props: {
      valueField: 'id'
    },
    apiParams: {
      size: 50,
      column: ['id', 'supplierName'],
      filter: [],
      order: [
        {
          column: 'supplierName',
          sort: true
        }
      ]
    }
  },
  /** 选择商品Spu */
  ProductSpu: {
    apiFn: findProductSpu,
    labelField: 'name',
    props: {
      valueField: 'id',
      keyField: 'id',
      childrenField: 'children'
    },
    apiParams: {
      size: 50,
      column: ['id', 'name', 'spuCode', 'parentSpu.id'],
      filter: []
    },
    transformer: (data: any[]) => {
      return buildTreeData(data, {
        parentKey: 'parentSpu.id',
        childrenKey: 'children',
        keyField: 'id'
      });
    }
  },
  /** 选择商品Sku */
  ProductSku: {
    apiFn: findProductSku,
    labelField: 'productName',
    props: {
      valueField: 'id',
      renderLabel: (option: any) => {
        return (
          // <NEllipsis>{[option.parentSpu?.name, option.productSpu?.name, option.productName].join(' / ')}</NEllipsis>
          <NPopover
            trigger="hover"
            v-slots={{
              trigger: () =>
                [option.productSpu?.parentSpu?.name, option.productSpu?.name, option.productName].join(' / ')
            }}
          >
            <span>{[option.productSpu?.parentSpu?.name, option.productSpu?.name, option.productName].join(' / ')}</span>
          </NPopover>
        );
      }
    },
    apiParams: {
      size: 50,
      column: ['id', 'skuCode', 'productName', 'productSpu.name', 'productSpu.parentSpu.name'],
      filter: [],
      order: [
        {
          column: 'productSpu.parentSpu.name',
          sort: true
        }
      ]
    },
    handleSearchFilter: searchText => {
      return [
        {
          column: null,
          type: 'andOr',
          value: [
            {
              column: 'skuCode',
              type: 'andLike',
              value: `%${searchText}%`
            },
            {
              column: 'productName',
              type: 'andLike',
              value: `%${searchText}%`
            },
            {
              column: 'productSpu.name',
              type: 'andLike',
              value: `%${searchText}%`
            }
          ]
        }
      ];
    }
  },
  /** 选择商品Sku */
  ProductSkuCode: {
    apiFn: findProductSku,
    labelField: 'skuCode',
    props: {
      valueField: 'id'
    },
    apiParams: {
      size: 50,
      column: ['id', 'skuCode'],
      filter: [],
      order: [
        {
          column: 'skuCode',
          sort: true
        }
      ]
    }
  },
  /** 选择门店物资类型 */
  MaterialType: {
    apiFn: findMaterialType,
    labelField: 'typeName',
    props: {
      valueField: 'id'
    },
    apiParams: {
      size: 50,
      column: ['id', 'typeName'],
      filter: [],
      order: [
        {
          column: 'typeName',
          sort: true
        }
      ]
    }
  },
  /** 选择门店物资类型 */
  MaterialItem: {
    apiFn: findMaterialItem,
    labelField: 'itemName',
    props: {
      valueField: 'id'
    },
    apiParams: {
      size: 50,
      column: ['id', 'itemName'],
      filter: [],
      order: [
        {
          column: 'itemName',
          sort: true
        }
      ]
    }
  },
  /** 选择消费者 */
  MemberInfo: {
    apiFn: findConsumer,
    labelField: 'account.userName',
    props: {
      valueField: 'id'
    },
    apiParams: {
      size: 50,
      column: ['id', 'account.userName'],
      filter: [],
      order: [
        {
          column: 'account.userName',
          sort: true
        }
      ]
    }
  },

  /** 选择消费者车辆 */
  MemberVehicleInfo: {
    apiFn: findVehicles,
    labelField: 'licensePlate',
    props: {
      valueField: 'id'
    },
    apiParams: {
      size: 50,
      column: ['id', 'licensePlate'],
      filter: [],
      order: [
        {
          column: 'licensePlate',
          sort: true
        }
      ]
    }
  },
  /** 选择服务类别 */
  ServiceType: {
    apiFn: findServiceType,
    labelField: 'serviceTypeName',
    props: {
      valueField: 'id',
      keyField: 'id',
      childrenField: 'children'
    },
    apiParams: {
      size: 50,
      column: ['id', 'serviceTypeName', 'parentServiceType.id'],
      filter: []
    },
    transformer: (data: any[]) => {
      return buildTreeData(data, {
        parentKey: 'parentServiceType.id',
        childrenKey: 'children',
        keyField: 'id'
      });
    }
  },
  /** 选择门店库存 */
  StoreInventoryInfo: {
    apiFn: findStoreInventory,
    labelField: 'inventoryCode',
    props: {
      valueField: 'id'
    },
    apiParams: {
      size: 50,
      column: ['id', 'inventoryCode'],
      filter: []
    }
  },

  /** 门店销售单 */
  StoreSaleOrder: {
    apiFn: findStoreSaleOrder,
    labelField: 'orderCode',
    props: {
      valueField: 'id'
    },
    apiParams: {
      size: 50,
      column: ['id', 'orderCode'],
      filter: []
    }
  },
  /** 服务项目 */
  ServiceItem: {
    apiFn: findServiceItem,
    labelField: 'serviceName',
    props: {
      valueField: 'id',
      renderLabel: (option: any) => {
        return <NEllipsis>{[option?.serviceType?.serviceTypeName, option?.serviceName].join(' / ')}</NEllipsis>;
      }
    },
    apiParams: {
      column: ['id', 'serviceName', 'serviceType.serviceTypeName'],
      filter: [],
      order: [
        {
          column: 'serviceType.serviceTypeName',
          sort: true
        },
        // {
        //   column: 'constructionSite.parentDictData.orderIndex',
        //   sort: true
        // },
        // {
        //   column: 'constructionSite.orderIndex',
        //   sort: true
        // },
        {
          column: 'serviceName',
          sort: true
        }
      ]
    }
  },
  /** 服务套餐 */
  ServicePackage: {
    apiFn: findServicePackage,
    labelField: 'packageName',
    props: {
      valueField: 'id'
    },
    apiParams: {
      column: ['id', 'packageName'],
      filter: []
    }
  },
  DistrictData: {
    apiFn: findDistrictData,
    labelField: 'name',
    props: {
      valueField: 'id'
    },
    apiParams: {
      column: ['id', 'name', 'parent.id', 'parent.name'],
      filter: []
    }
  },
  MiniAppConfig: {
    apiFn: findMiniAppConfig,
    labelField: 'appid',
    props: {
      valueField: 'id'
    },
    apiParams: {
      column: ['id', 'appid'],
      filter: []
    }
  },
  MiniAppTemplate: {
    apiFn: findMiniAppTemplate,
    labelField: 'templateName',
    props: {
      valueField: 'id'
    },
    apiParams: {
      column: ['id', 'templateName'],
      filter: []
    }
  },
  Warehouse: {
    apiFn: findWarehouse,
    labelField: 'name',
    props: {
      valueField: 'id'
    },
    apiParams: {
      column: ['id', 'name'],
      filter: []
    }
  }
} as const;

/** 获取apiSelect配置,并重新设置过滤条件及排序条件 */
export function getApiSelectConfig(
  key: OptionKeys,
  params?: {
    column?: string[];
    filter?: Api.Common.Filter[];
    order?: Api.Common.Order[];
    extParams?: Record<string, any>;
    labelField?: string;
    valueField?: string;
    tooltip?: boolean;
    /** 数据转换 */
    transformer?: (data: any[]) => any[];
  }
): RemoteOptionsConfig {
  const config = _.cloneDeep(ApiSelectConfig[key]);
  if (params) {
    const { filter, order, column, extParams, labelField, valueField, tooltip, transformer } = params;
    if (filter && filter.length > 0) {
      config.apiParams.filter = filter;
    }
    if (order && order.length > 0) {
      config.apiParams.order = order;
    }
    if (column && column.length > 0) {
      config.apiParams.column = _.union(config.apiParams.column, column);
    }
    if (extParams) {
      config.apiParams.extParams = extParams;
    }
    if (labelField) {
      config.labelField = labelField;
    }
    if (transformer) {
      config.transformer = transformer;
    }
    if (valueField) {
      if (config.props) {
        _.set(config.props, 'valueField', valueField);
      } else {
        config.props = { valueField };
      }
    }
    if (tooltip) {
      config.tooltip = tooltip;
    }
  }
  return config;
}
